import React, { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { alpha, useTheme } from '@mui/material/styles';
import type { CertStatusDetail as CertStatusDetailData } from 'src/types/CertStatus';
import type { CertStatusActionId } from 'src/types/CertStatus';

interface CertStatusDetailProps {
  detail?: CertStatusDetailData | null;
  /** Filter actions to this household member (user id). */
  selectedUserId?: number | null;
  /** Filter question actions to this cert member id. */
  selectedCertMemberId?: number | null;
  loading?: boolean;
  /** Cert kind label (e.g. "application") used in the "Interview Complete"
   *  message shown when the member has no outstanding actions. Omit to keep
   *  the existing behaviour of rendering no completion message. ENG-3894: the
   *  message is also suppressed when the cert's phase is `terminal_approved`,
   *  where `status_text` already states the outcome and the two co-rendered. */
  completeStateCertKind?: string | null;
  /** When provided, every action item becomes tappable and calls this with its
   *  id so the caller can route to the relevant surface. */
  onAction?: (id: CertStatusActionId) => void;
}

interface ActionItem {
  id: CertStatusActionId;
  icon: ReactNode;
  tint: string;
  label: string;
  detail?: string;
}

const CertStatusDetail: FC<CertStatusDetailProps> = ({
  detail,
  selectedUserId,
  selectedCertMemberId,
  loading,
  completeStateCertKind,
  onAction,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Stack gap={1.5}>
        {/* status_text paragraph */}
        <Stack gap={0.75}>
          <Skeleton variant="text" width="100%" height={18} />
          <Skeleton variant="text" width="82%" height={18} />
          <Skeleton variant="text" width="58%" height={18} />
        </Stack>
        {/* action items */}
        <Stack
          gap={1.25}
          sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}
        >
          <Skeleton variant="text" width={120} height={12} />
          {[0, 1].map((i) => (
            <Stack key={i} direction="row" alignItems="center" gap={1.25}>
              <Skeleton
                variant="rounded"
                width={34}
                height={34}
                sx={{ borderRadius: '9px' }}
              />
              <Skeleton variant="text" width="70%" height={15} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  }

  if (!detail) {
    return null;
  }

  const ai = detail.action_items;
  const items: ActionItem[] = [];

  const questionEntry =
    selectedCertMemberId != null
      ? ai.missing_questions.by_cert_member?.[String(selectedCertMemberId)]
      : undefined;
  if (questionEntry && questionEntry.count > 0) {
    items.push({
      id: 'questions',
      icon: (
        <AssignmentOutlinedIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
      ),
      tint: alpha(theme.palette.warning.main, 0.12),
      label: `Complete about ${questionEntry.count} question${
        questionEntry.count === 1 ? '' : 's'
      }`,
    });
  }

  const signatureEntry =
    selectedUserId != null
      ? ai.pending_signatures.by_recipient?.[String(selectedUserId)]
      : undefined;
  if (signatureEntry && signatureEntry.count > 0) {
    items.push({
      id: 'signatures',
      icon: <DrawOutlinedIcon sx={{ fontSize: 18, color: 'error.main' }} />,
      tint: alpha(theme.palette.error.main, 0.12),
      label: `Sign ${signatureEntry.count} document${
        signatureEntry.count === 1 ? '' : 's'
      }`,
      detail: (signatureEntry.documents ?? []).join(' · '),
    });
  }

  if (
    selectedCertMemberId != null &&
    ai.profile_gaps_cert_member_ids?.includes(selectedCertMemberId)
  ) {
    items.push({
      id: 'profile_gaps',
      icon: (
        <AccountCircleOutlinedIcon sx={{ fontSize: 18, color: 'info.dark' }} />
      ),
      tint: alpha(theme.palette.info.main, 0.12),
      label: 'Complete your profile',
    });
  }

  if (
    selectedCertMemberId != null &&
    ai.rejected_docs_cert_member_ids?.includes(selectedCertMemberId)
  ) {
    items.push({
      id: 'rejected_docs',
      icon: (
        <ReportProblemOutlinedIcon
          sx={{ fontSize: 18, color: 'warning.dark' }}
        />
      ),
      tint: alpha(theme.palette.warning.main, 0.12),
      label: 'A document needs your attention',
    });
  }

  if (
    selectedCertMemberId != null &&
    ai.insufficient_files_cert_member_ids?.includes(selectedCertMemberId)
  ) {
    items.push({
      id: 'insufficient_files',
      icon: (
        <UploadFileOutlinedIcon sx={{ fontSize: 18, color: 'info.dark' }} />
      ),
      tint: alpha(theme.palette.info.main, 0.12),
      label: 'Upload missing documents',
    });
  }

  return (
    <Stack gap={1.5}>
      <Typography sx={{ fontSize: 14, lineHeight: 1.5, fontWeight: 500 }}>
        {detail.status_text}
      </Typography>

      {items.length > 0 ? (
        <Stack
          gap={1.25}
          sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.02em',
              color: 'text.secondary',
            }}
          >
            What you still need to do
          </Typography>
          {items.map((item) => {
            const actionable = onAction != null;
            return (
              <Stack
                key={item.id}
                direction="row"
                alignItems="center"
                gap={1.25}
                role={actionable ? 'button' : undefined}
                tabIndex={actionable ? 0 : undefined}
                onClick={actionable ? () => onAction?.(item.id) : undefined}
                onKeyDown={
                  actionable
                    ? (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onAction?.(item.id);
                        }
                      }
                    : undefined
                }
                sx={
                  actionable
                    ? {
                        cursor: 'pointer',
                        borderRadius: '9px',
                        p: 0.5,
                        m: -0.5,
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                        },
                      }
                    : undefined
                }
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: item.tint,
                  }}
                >
                  {item.icon}
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  {item.detail ? (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.detail}
                    </Typography>
                  ) : null}
                </Stack>
                {actionable && (
                  <ChevronRightIcon
                    sx={{
                      fontSize: 18,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      ) : completeStateCertKind && detail.phase !== 'terminal_approved' ? (
        <Stack
          direction="row"
          alignItems="flex-start"
          gap={2}
          sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 28, color: 'success.main', mt: 0.25 }}
          />
          <Stack gap={0.5}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
              Interview Complete
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              Your {completeStateCertKind} has been submitted. A compliance
              specialist will review it and contact you via email or SMS if
              anything else is needed.
            </Typography>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default CertStatusDetail;
